import { Close } from '@beamery/lib-ds-icons';
import { useHover } from '@react-aria/interactions';
import { useSearchField } from '@react-aria/searchfield';
import { mergeProps } from '@react-aria/utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSearchFieldState } from '@react-stately/searchfield';
import React, { useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useTheme } from '../../hooks/index.js';
import { omit } from '../../utils/index.js';
import { Field } from '../field/index.js';
import { InputLoading } from './input-loading.styles.js';
import { InputElement } from './input.styles.js';
import { CloseButtonElement, SearchContainerElement, SearchIconElement, } from './search-field.styles.js';
export const SearchField = React.forwardRef(({ dataAutomation = 'searchfield', description, fullWidth, ...props }, forwardedRef) => {
    const theme = useTheme();
    const state = useSearchFieldState({
        description: typeof description === 'function' ? undefined : description,
        ...props,
    });
    const inputRef = useRef(null);
    const ref = useMergedRef(inputRef, forwardedRef);
    const onDebouncedChange = useDebouncedCallback((search) => {
        props.onDebouncedChange?.(search);
    }, theme.debounceTime[props.debounceTime || 'none']);
    const onClear = useCallback(() => {
        props.onClear?.();
        onDebouncedChange('');
    }, [props.onClear, onDebouncedChange]);
    const { labelProps, inputProps, clearButtonProps, descriptionProps, errorMessageProps, } = useSearchField({
        ...props,
        validationState: !props.errorMessage ? 'valid' : 'invalid',
        onClear,
    }, state, inputRef);
    const { hoverProps } = useHover(props);
    const onChange = useCallback((event) => {
        inputProps.onChange?.(event);
        onDebouncedChange(event.target.value);
    }, [onDebouncedChange, inputProps.onChange]);
    const shouldShowIcon = !props.noIcon;
    const shouldShowCloseButton = state.value !== '' && !props.noAction && !props.loading;
    return (React.createElement(Field, { ...omit(mergeProps(props, inputProps), 'onKeyDown', 'onBlur', 'id', 'role', 'aria-labelledby', 'aria-required'), containerRef: props.containerRef, label: {
            children: props.label,
            hiddenLabel: props.hiddenLabel,
            ...labelProps,
        }, error: {
            ...errorMessageProps,
            message: props.errorMessage,
        }, description: {
            ...descriptionProps,
            message: typeof description === 'function' ? description() : description,
        }, dataAutomation: dataAutomation },
        React.createElement(SearchContainerElement, { ref: props.inputContainerRef, isErrored: !!props.errorMessage, isDisabled: !!props.isDisabled, isOnGray: !!props.isOnGray, fullWidth: fullWidth },
            shouldShowIcon && React.createElement(SearchIconElement, null),
            props.loading ? (React.createElement(InputLoading, null, props.value || props.placeholder)) : (React.createElement(InputElement, { ref: ref, ...mergeProps(omit(inputProps, 'onChange'), hoverProps), onChange: onChange, type: 'text', "data-automation": `${dataAutomation}/input`, role: props.role, tabIndex: props.tabIndex, "aria-controls": props['aria-controls'], "aria-expanded": props['aria-expanded'] })),
            shouldShowCloseButton && (React.createElement(CloseButtonElement, { "data-automation": `${dataAutomation}/close`, ...clearButtonProps },
                React.createElement(Close, { size: 'standard', color: 'inherit' }))))));
});
//# sourceMappingURL=search-field.component.js.map